<script lang="ts">
  import Card from "$app/Pages/Signup/Card.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import { catchError, formatter, toast, xhr } from "$app/utils"
  import Coupon from "$app/Pages/Signup/Coupon.svelte"
  import SaveButton from "$app/Pages/Signup/SaveButton.svelte"
  import BillingIntervalButton from "$app/Pages/Signup/BillingIntervalButton.svelte"
  import MemberCount from "$app/Pages/Signup/MemberCount.svelte"

  let saving = $state(false)
  let card = $state<Card>(null)
  let errors: Record<string, any> = $state({})
  let name = $state("")

  async function getData(): Promise<Record<string, any> | false> {
    const cardToken = await card.getStripeToken()
    if (cardToken === "") {
      alert(
        "There was an error with the credit card verification. Please try again."
      )
      saving = false
      return false
    }

    return {
      selected_plan_id: signupStore.selectedPlan.id,
      plan: { name: name },
      subscription: {
        full_access_total: signupStore.signupCount("full_access"),
        team_member_total: signupStore.signupCount("team_member"),
        recognition_member_total: signupStore.signupCount("recognition_member"),
        billing_interval: signupStore.billingInterval,
        subscription_type: signupStore.selectedPlan.id,
        name: signupStore.selectedPlan.name,
        stripe_coupon: signupStore.coupon.id,
        stripe_token: cardToken
      }
    }
  }

  async function handleSubmit() {
    saving = true

    const data = await getData()

    const [error, resp] = await catchError(
      xhr.post("/plans", {
        ...data
      })
    )
    if (error) {
      errors = error?.response?.data?.errors || {}
      saving = false
      let msg = "There are some errors. Please verify and try again."
      if (errors.name || errors.email || errors.password) {
        msg =
          "There are some errors on your account details. Please verify and try again"
      }
      toast(msg, "error", {
        duration: 15 * 60 * 1000
      })
      return
    }

    errors = {}
    saving = false
    if (resp.data?.redirection_path) {
      toast("Thank you for signing up!", "success")
      window.location.href = resp.data.redirection_path
    }
  }
</script>

<div class="bg-gray-50 order-1 md:order-2">
  <hr class="text-gray-200" />

  <div class="px-2 md:px-4">
    <div class="mt-4 input-cont" class:error={errors.name}>
      <label for="company_name">Company Name</label>
      <input
        type="text"
        bind:value={name}
        class="input"
        id="company_name"
        placeholder="My Company INC."
      />
      <div class="msg">{errors.name}</div>
    </div>

    <div class="grid grid-cols-3 gap-y-4 my-5">
      {#each signupStore.member_roles as role (role.id)}
        <MemberCount role={role.id} min={role.min} max={role.max} />
      {/each}
    </div>

    <div class="relative mt-6 flex justify-center">
      <BillingIntervalButton />
    </div>

    <div class="grid grid-cols-3 gap-2 mt-4">
      <div class="col-span-2">Subtotal</div>
      <div class="font-bold text-right">
        {formatter.format(signupStore.totalAmount)}
      </div>
    </div>

    <div class="grid grid-cols-3 gap-2">
      <div class="col-span-2 flex items-center gap-2">
        Discount
        <Coupon onvalidating={(validating) => (saving = validating)} />
      </div>
      <div class="font-bold text-right leading-12">
        - {formatter.format(signupStore.discount)}
      </div>
    </div>

    <hr class="text-gray-200 mt-2" />
    <div class="mt-4 text-xl leading-6 font-medium grid grid-cols-2 gap-3">
      <div>
        {#if signupStore.billingInterval === "month"}
          Monthly Total
        {:else if signupStore.billingInterval === "year"}
          Annual Total
        {/if}
      </div>
      <div class="font-semibold text-right">
        {formatter.format(signupStore.total)}
      </div>
    </div>

    {#if signupStore.selectedPlan.name !== "Free"}
      <div class="text-sm mt-2 text-right text-green-600">
        {#if signupStore.billingInterval == "year"}
          You save 18% by paying annually
        {:else}
          You could save 18% by paying annually
        {/if}
      </div>
    {/if}
  </div>

  <div class="px-2 py-4 md:px-4">
    <Card bind:this={card} />
  </div>

  <div class="px-4 py-2">
    <SaveButton
      bind:saving
      onclick={handleSubmit}
      title="Create Standard Plan"
    />
  </div>
</div>
